import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface TabItem {
  id: string;
  name: string;
}

interface ScrollableTabsProps {
  tabs: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children?: React.ReactNode;
  className?: string;
}

export const ScrollableTabs = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Root>, ScrollableTabsProps>(
  ({ tabs, defaultValue, value, onValueChange, children, className }, ref) => {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = React.useState(false);
    const [showRightArrow, setShowRightArrow] = React.useState(false);

    // Check if scrolling is needed
    const checkScrollPosition = React.useCallback(() => {
      if (!scrollContainerRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1); // -1 to account for rounding errors
    }, []);

    // Scroll horizontally
    const scroll = (direction: "left" | "right") => {
      if (!scrollContainerRef.current) return;

      const scrollAmount = 200; // Scroll amount can be adjusted as needed
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === "left" ? currentScroll - scrollAmount : currentScroll + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    };

    // Handle wheel event for horizontal scrolling
    const handleWheel = React.useCallback(
      (e: WheelEvent) => {
        if (!scrollContainerRef.current) return;

        // Prevent the default vertical scroll
        e.preventDefault();

        // Use the natural direction for horizontal scroll, but the vertical scroll direction is inverted
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : -e.deltaY;

        // Apply the delta to scroll in the correct direction
        scrollContainerRef.current.scrollLeft += delta;

        // Check scroll position after scrolling
        checkScrollPosition();
      },
      [checkScrollPosition]
    );

    // Check scroll position on mount and when tabs change
    React.useEffect(() => {
      checkScrollPosition();

      // Add resize and scroll event listeners
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        scrollContainer.addEventListener("scroll", checkScrollPosition);
        window.addEventListener("resize", checkScrollPosition);

        // Add wheel event listener for horizontal scrolling
        scrollContainer.addEventListener("wheel", handleWheel, { passive: false });
      }

      // Cleanup
      return () => {
        if (scrollContainer) {
          scrollContainer.removeEventListener("scroll", checkScrollPosition);
          scrollContainer.removeEventListener("wheel", handleWheel);
        }
        window.removeEventListener("resize", checkScrollPosition);
      };
    }, [checkScrollPosition, handleWheel, tabs]);

    return (
      <div className="w-full max-w-full">
        <TabsPrimitive.Root
          ref={ref}
          defaultValue={defaultValue || tabs[0]?.id}
          value={value}
          onValueChange={onValueChange}
          className={cn("w-full max-w-full", className)}
        >
          <div className="border-b border-gray-200 w-full max-w-full relative">
            <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-hide max-w-full">
              <TabsPrimitive.List className="flex min-w-max px-4">
                {tabs.map((tab) => (
                  <TabsPrimitive.Trigger
                    key={tab.id}
                    value={tab.id}
                    className={cn(
                      "px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 data-[state=active]:border-teal-500 data-[state=active]:text-teal-600 border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 focus:outline-none"
                    )}
                  >
                    {tab.name}
                  </TabsPrimitive.Trigger>
                ))}
              </TabsPrimitive.List>
            </div>

            {/* Left shadow overlay */}
            {showLeftArrow && (
              <div className="absolute left-0 top-0 bottom-0 w-24 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent"></div>
              </div>
            )}

            {/* Right shadow overlay */}
            {showRightArrow && (
              <div className="absolute right-0 top-0 bottom-0 w-24 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-l from-white to-transparent"></div>
              </div>
            )}

            {/* Left control button */}
            {showLeftArrow && (
              <button
                onClick={() => scroll("left")}
                className="absolute left-1 top-1/2 -translate-y-1/2 z-10 p-1 bg-teal-600/60 hover:bg-teal-600/90 rounded-full shadow-sm transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
            )}

            {/* Right control button */}
            {showRightArrow && (
              <button
                onClick={() => scroll("right")}
                className="absolute right-1 top-1/2 -translate-y-1/2 z-10 p-1 bg-teal-600/60 hover:bg-teal-600/90 rounded-full shadow-sm transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            )}
          </div>

          <div className="max-w-full overflow-hidden">{children}</div>
        </TabsPrimitive.Root>
      </div>
    );
  }
);

ScrollableTabs.displayName = "ScrollableTabs";

// Add CSS for hiding scrollbar
if (typeof document !== "undefined") {
  const scrollbarStyle = document.createElement("style");
  scrollbarStyle.textContent = `
    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }
    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `;
  document.head.appendChild(scrollbarStyle);
}
